import { refreshToken } from '@/composables/useAuth'
import useAuthStore from '@/stores/authStore'
import getEnvironment from '@/utils/env'

/**
 * Direct client for nova-ada-api. Same token discipline as `api.ts`: refresh via ada-api when
 * `exp` has passed, current CustomerUserToken in `X-API-Key`. Different response discipline: a
 * Nova 401 means "no Nova identity in the token", never an expired session, so this client never
 * clears tokens — callers get `{ ok: false }` and treat Nova as unavailable. (ADA-4442)
 */

export interface NovaResponse<T> {
  ok: boolean
  status: number
  /** Parsed JSON body, also for error responses (Nova's `message` lives there); null when absent */
  data: T | null
}

interface NovaFetchInit {
  method?: 'GET' | 'PATCH' | 'POST'
  body?: unknown
}

const REQUEST_TIMEOUT_MS = 15_000

/** Same rule as ada-api's NOVA_ADA_API_URL: env override, else derived from the environment. */
function getNovaApiRoot(): string {
  const override = import.meta.env.VITE_APP_NOVA_API_ROOT
  if (override != null && override !== '') return override.replace(/\/$/, '')
  const environment = getEnvironment()?.id
  if (environment === 'production') return 'https://nova-api.amili.se'
  if (environment === 'localhost') return 'http://localhost:5500'
  return 'https://nova-api-test.amili.se'
}

const NOVA_API_ROOT = getNovaApiRoot()

export const NOVA_CREDIT_BALANCES_PATH = '/debtor/credit-balances'
export const NOVA_BANK_DETAILS_PATH = '/debtor/credit-balances/bank-details'

export function getNovaStatusPath(minibankNr: number): string {
  return `/debtor/credit-balances/${minibankNr}/status`
}

export function isNovaEnabled(): boolean {
  return NOVA_API_ROOT !== ''
}

export async function novaFetch<T>(path: string, init: NovaFetchInit = {}): Promise<NovaResponse<T>> {
  try {
    let response = await sendRequest(path, init, await getValidToken())
    if (response.status === 401) {
      // A fresh token without a Nova subject stays 401 after a refresh — only retry when a
      // refresh can change the outcome, or every load would trigger one.
      if (!isRefreshWorthwhile()) return { ok: false, status: 401, data: null }
      const refreshed = await refreshToken()
      if (refreshed == null) return { ok: false, status: 401, data: null }
      response = await sendRequest(path, init, refreshed)
      if (response.status === 401) return { ok: false, status: 401, data: null }
    }
    return { ok: response.ok, status: response.status, data: await readJson<T>(response) }
  } catch {
    // Network failure or timeout — indistinguishable from Nova being down.
    return { ok: false, status: 0, data: null }
  }
}

function isRefreshWorthwhile(): boolean {
  const claims = useAuthStore().selectedToken?.payload
  if (claims == null) return true
  if (claims.exp * 1000 <= Date.now()) return true
  return claims.subject_id_nbr == null
}

async function getValidToken(): Promise<string | null> {
  const authStore = useAuthStore()
  const exp = authStore.selectedToken?.payload?.exp
  if (exp != null && exp * 1000 < Date.now()) {
    await refreshToken()
  }
  return authStore.selectedToken?.token ?? null
}

async function sendRequest(path: string, init: NovaFetchInit, token: string | null): Promise<Response> {
  const headers: Record<string, string> = { 'Cache-Control': 'no-cache' }
  if (token != null) headers['X-API-Key'] = token
  if (init.body != null) headers['Content-Type'] = 'application/json'
  // AbortController instead of AbortSignal.timeout, which older Safari lacks.
  const controller = new AbortController()
  const timer = setTimeout(() => {
    controller.abort()
  }, REQUEST_TIMEOUT_MS)
  try {
    return await fetch(`${NOVA_API_ROOT}${path}`, {
      method: init.method ?? 'GET',
      headers,
      body: init.body == null ? undefined : JSON.stringify(init.body),
      signal: controller.signal,
    })
  } finally {
    clearTimeout(timer)
  }
}

async function readJson<T>(response: Response): Promise<T | null> {
  try {
    return (await response.json()) as T
  } catch {
    return null
  }
}
